import { parseCapture, type CaptureParse, type TagToken } from '@flow/capture-parser';
import { normalizeTagName, type CaptureItemInput } from '@flow/domain';
import type { CaptureTagRow, CaptureTaxonomyRow } from '@flow/sync';
import type { CaptureChip } from '@flow/ui/components';

import { t } from '../i18n/es.js';

/**
 * Pure part of the quick capture in the web (design D3): today in the user's time zone, the
 * parsed tags resolved against the existing ones, and the chips the sheet shows.
 */

export const DEFAULT_TIME_ZONE = 'Europe/Madrid';

/** Today as `YYYY-MM-DD` in a time zone (the parser works with calendar dates only). */
export function todayIn(timeZone: string | null | undefined, now = new Date()): string {
  const format = (zone: string) =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  try {
    return format(timeZone ?? DEFAULT_TIME_ZONE);
  } catch {
    // An unknown or empty zone name in the preferences must not break the capture.
    return format(DEFAULT_TIME_ZONE);
  }
}

export interface TagIndex {
  taxonomies: Map<string, CaptureTaxonomyRow>;
  /** `taxonomy|parent|normalized name` → tag id (merged tags point to their target). */
  tags: Map<string, string>;
}

const tagKey = (taxonomyId: string, parentId: string | null, name: string) =>
  `${taxonomyId}|${parentId ?? ''}|${normalizeTagName(name)}`;

export function buildTagIndex(taxonomies: CaptureTaxonomyRow[], tags: CaptureTagRow[]): TagIndex {
  const index: TagIndex = { taxonomies: new Map(), tags: new Map() };
  for (const taxonomy of taxonomies) index.taxonomies.set(taxonomy.prefix, taxonomy);
  for (const tag of tags) {
    const key = tagKey(tag.taxonomy_id, tag.parent_id, tag.name);
    // A merged tag and its target can share a name: the target wins.
    if (tag.merged_into_id === null || !index.tags.has(key)) {
      index.tags.set(key, tag.merged_into_id ?? tag.id);
    }
  }
  return index;
}

export type CaptureTagInput = NonNullable<CaptureItemInput['tags']>[number];

export interface ResolvedTag {
  token: TagToken;
  input: CaptureTagInput;
  /** Some segment of the path does not exist yet and will be created. */
  isNew: boolean;
}

/** Resolves a parsed tag against the index; null when the workspace has no taxonomy for it. */
export function resolveTag(token: TagToken, index: TagIndex): ResolvedTag | null {
  const taxonomy = index.taxonomies.get(token.prefix);
  if (!taxonomy) return null;
  let parentId: string | null = null;
  let isNew = false;
  const path: CaptureTagInput['path'] = token.path.map((name) => {
    // Under a new tag everything is new.
    const existing = isNew ? undefined : index.tags.get(tagKey(taxonomy.id, parentId, name));
    if (existing) {
      parentId = existing;
      return { name, existing_id: existing };
    }
    isNew = true;
    return { name };
  });
  return {
    token,
    input: {
      taxonomy: {
        id: taxonomy.id,
        workspace_id: taxonomy.workspace_id,
        cardinality: taxonomy.cardinality,
      },
      path,
    },
    isNew,
  };
}

export interface CaptureInterpretation {
  parse: CaptureParse;
  tags: ResolvedTag[];
  chips: CaptureChip[];
}

/**
 * Parses the text and resolves its tags. A prefix without a taxonomy in the workspace is not a
 * tag here: its text stays in the title.
 */
export function interpret(
  text: string,
  options: { today: string; dismissed: readonly string[]; index: TagIndex },
): CaptureInterpretation {
  let parse = parseCapture(text, { today: options.today, dismissed: options.dismissed });
  const orphan = parse.tags.filter((tag) => !options.index.taxonomies.has(tag.prefix));
  if (orphan.length > 0) {
    parse = parseCapture(text, {
      today: options.today,
      dismissed: [...options.dismissed, ...orphan.map((tag) => tag.key)],
    });
  }
  const tags = parse.tags
    .map((token) => resolveTag(token, options.index))
    .filter((tag): tag is ResolvedTag => tag !== null);
  const newByKey = new Map(tags.map((tag) => [tag.token.key, tag.isNew]));
  const chips = parse.tokens.map((token): CaptureChip => {
    if (token.kind === 'tag') {
      const isNew = newByKey.get(token.key) ?? false;
      return {
        key: token.key,
        start: token.start,
        end: token.end,
        label: t.quickCapture.tagLabel[token.prefix],
        value: `${token.prefix}${token.path.join('/')}`,
        ...(isNew ? { isNew } : {}),
      };
    }
    return {
      key: token.key,
      start: token.start,
      end: token.end,
      label: token.kind === 'due' ? t.quickCapture.due : t.quickCapture.start,
      value: formatDay(token.date, options.today),
    };
  });
  return { parse, tags, chips };
}

/** «sáb 12 sep», with the year when it is not this year's. */
export function formatDay(date: string, today: string): string {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  const base = `${t.quickCapture.weekdays[weekday]} ${day} ${t.quickCapture.months[month - 1]}`;
  return today.startsWith(String(year)) ? base : `${base} ${year}`;
}
